import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { CatalogProduct } from '@/types'

import { PackageSearch, RefreshCw, Search } from 'lucide-react'

import {
    useEffect,
    useId,
    useMemo,
    useRef,
    useState
} from 'react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useSettings } from '@/components/generals/settings-provider'
import { getStockStatus, normalizeText } from '@/lib/format'

interface ProductPickerProps {
    inputId: string
    products: CatalogProduct[]
    status: 'idle' | 'loading' | 'error' | 'ready'
    onRetry: () => void
    onSelect: (product: CatalogProduct) => void
    invalid?: boolean
    describedById?: string
}

const DOT = {
    ok: 'bg-success',
    low: 'bg-warning',
    out: 'bg-danger'
}

const MAX_RESULTS = 60

const ProductPicker: FC<ProductPickerProps> = ({
    inputId,
    products,
    status,
    onRetry,
    onSelect,
    invalid = false,
    describedById
}): ReactNode => {
    const { number } = useSettings()
    const listId = useId()
    const [query, setQuery] = useState('')
    const [activeIndex, setActiveIndex] = useState(0)
    const optionRefs = useRef<(HTMLLIElement | null)[]>([])

    const results = useMemo(() => {
        const search = normalizeText(query)

        if (!search) return products.slice(0, MAX_RESULTS)

        return products
            .filter(product => normalizeText(product.name).includes(search) || normalizeText(product.sku).includes(search))
            .slice(0, MAX_RESULTS)
    }, [products, query])

    useEffect(() => {
        setActiveIndex(0)
    }, [query])

    useEffect(() => {
        optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' })
    }, [activeIndex])

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (!results.length) return

        if (event.key === 'ArrowDown') {
            event.preventDefault()
            setActiveIndex(index => Math.min(index + 1, results.length - 1))
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault()
            setActiveIndex(index => Math.max(index - 1, 0))
        }

        if (event.key === 'Enter') {
            event.preventDefault()

            const product = results[activeIndex]

            if (product) onSelect(product)
        }
    }

    const activeProduct = results[activeIndex]
    const isLoading = status === 'loading' || status === 'idle'

    return (
        <div
            className={cn(
                'overflow-hidden rounded-lg border bg-background',
                invalid ? 'border-destructive ring-3 ring-destructive/20' : 'border-input'
            )}
        >
            <div className='relative border-b'>
                <Search className='pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />

                <input
                    id={inputId}
                    type='text'
                    role='combobox'
                    aria-expanded='true'
                    aria-controls={listId}
                    aria-autocomplete='list'
                    aria-activedescendant={activeProduct ? `${listId}-${activeProduct.id}` : undefined}
                    aria-invalid={invalid || undefined}
                    aria-describedby={describedById}
                    autoComplete='off'
                    placeholder='Busca por nombre o código'
                    value={query}
                    onChange={event => setQuery(event.target.value)}
                    onKeyDown={handleKeyDown}
                    className='h-10 w-full bg-transparent pr-3 pl-9 text-base outline-none placeholder:text-muted-foreground md:text-sm'
                />
            </div>

            {status === 'error' && (
                <div className='flex flex-col items-center gap-2 px-4 py-6 text-center'>
                    <p className='text-sm text-muted-foreground'>
                        No se pudo cargar la lista de productos.
                    </p>

                    <Button type='button' variant='outline' size='sm' onClick={onRetry}>
                        <RefreshCw aria-hidden='true' />
                        Reintentar
                    </Button>
                </div>
            )}

            {isLoading && (
                <div className='flex flex-col gap-2 p-3' aria-label='Cargando productos'>
                    <Skeleton className='h-9 w-full' />
                    <Skeleton className='h-9 w-full' />
                    <Skeleton className='h-9 w-4/5' />
                </div>
            )}

            {status === 'ready' && !results.length && (
                <div className='flex flex-col items-center gap-2 px-4 py-6 text-center'>
                    <PackageSearch className='size-5 text-muted-foreground' aria-hidden='true' />

                    <p className='text-sm text-muted-foreground'>
                        {products.length ? 'Ningún producto coincide con tu búsqueda.' : 'Todavía no hay productos. Crea uno primero.'}
                    </p>
                </div>
            )}

            <ul
                id={listId}
                role='listbox'
                aria-label='Productos'
                className={cn('max-h-56 overflow-y-auto p-1', (status !== 'ready' || !results.length) && 'hidden')}
            >
                {status === 'ready' && results.map((product, index) => {
                    const stockStatus = getStockStatus(product.quantity, product.minStock)
                    const active = index === activeIndex

                    return (
                        <li
                            key={product.id}
                            id={`${listId}-${product.id}`}
                            ref={element => {
                                optionRefs.current[index] = element
                            }}
                            role='option'
                            aria-selected={active}
                            onMouseDown={event => event.preventDefault()}
                            onMouseEnter={() => setActiveIndex(index)}
                            onClick={() => onSelect(product)}
                            className={cn(
                                'flex cursor-pointer items-center justify-between gap-3 rounded-md px-2.5 py-2',
                                active ? 'bg-accent text-accent-foreground' : 'text-foreground'
                            )}
                        >
                            <div className='min-w-0'>
                                <p className='truncate text-sm font-medium'>
                                    <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                                </p>

                                {product.sku && (
                                    <p className='truncate font-mono text-xs text-muted-foreground'>
                                        <span data-mdlfy-cms={`products:${product.id}:sku`}>{product.sku}</span>
                                    </p>
                                )}
                            </div>

                            <div className='flex shrink-0 items-center gap-2 text-xs text-muted-foreground tabular-nums'>
                                <span className={cn('size-2 rounded-full', DOT[stockStatus])} aria-hidden='true' />
                                <span>{number(product.quantity)}</span>
                                <span data-mdlfy-cms={`products:${product.id}:unit`}>{product.unit}</span>
                            </div>
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}

export default ProductPicker
